import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { http } from "../api";
import { PageHeader, PersonLink } from "../shared";
import { MapPin, Siren, Radio, Flame, X, ArrowUpRight, Users } from "lucide-react";

const NABADWIP = [23.4083, 88.3722];

// Custom marker icons
const iconFor = (status) => L.divIcon({
  className: "live-marker",
  html: `<div class="lm ${status === "sos" ? "lm-sos" : "lm-online"}">${status === "sos" ? "!" : ""}</div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

export default function LiveMap({ user, setPage, setPageContext }) {
  const [users, setUsers] = useState([]);
  const [sos, setSos] = useState([]);
  const [error, setError] = useState("");
  const [selfPos, setSelfPos] = useState(null);
  const [workload, setWorkload] = useState({ booths: [], totals: {} });
  const [drawer, setDrawer] = useState(null);   // { booth, ward, band } placeholder while fetching
  const [drawerDetail, setDrawerDetail] = useState(null); // full server response
  const pingRef = useRef(null);

  useEffect(() => {
    if (!drawer) { setDrawerDetail(null); return undefined; }
    let stale = false;
    setDrawerDetail(null);
    http.get(`/field-ops/booth/${encodeURIComponent(drawer.booth)}`)
      .then((r) => { if (!stale) setDrawerDetail(r.data); })
      .catch(() => { if (!stale) setDrawerDetail({ error: "Could not load booth detail" }); });
    return () => { stale = true; };
  }, [drawer]);

  // Poll for other users every 15s
  useEffect(() => {
    const fetchLive = async () => {
      try {
        const [u, s, wl] = await Promise.all([
          http.get("/live/users"),
          http.get("/live/sos"),
          http.get("/field-ops/booth-workload"),
        ]);
        setUsers(u.data); setSos(s.data.filter((x) => !x.resolved));
        setWorkload(wl.data);
      } catch { /* silent */ }
    };
    fetchLive();
    const t = setInterval(fetchLive, 15000);
    return () => clearInterval(t);
  }, []);

  // Send own position every 15s (if permitted)
  useEffect(() => {
    if (!navigator.geolocation) { setError("Geolocation not supported by browser"); return; }
    const ping = () => {
      navigator.geolocation.getCurrentPosition(
        async (p) => {
          setSelfPos([p.coords.latitude, p.coords.longitude]);
          try { await http.post("/live/ping", { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, status: "online" }); }
          catch { /* ignore */ }
        },
        (e) => setError(`Location: ${e.message}`),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
      );
    };
    ping();
    pingRef.current = setInterval(ping, 15000);
    return () => clearInterval(pingRef.current);
  }, []);

  const raiseSos = async () => {
    if (!selfPos) { setError("Cannot raise SOS without location. Please grant permission and retry."); return; }
    try {
      await http.post("/live/sos", { lat: selfPos[0], lng: selfPos[1], status: "sos" });
      const [u, s] = await Promise.all([http.get("/live/users"), http.get("/live/sos")]);
      setUsers(u.data); setSos(s.data.filter((x) => !x.resolved));
    } catch { setError("SOS request failed"); }
  };

  const resolveSos = async (sos_id) => {
    await http.patch(`/live/sos/${sos_id}/resolve`);
    const [u, s] = await Promise.all([http.get("/live/users"), http.get("/live/sos")]);
    setUsers(u.data); setSos(s.data.filter((x) => !x.resolved));
  };

  const activeCount = users.filter((u) => u.status !== "sos").length;
  const sosCount = sos.length;

  return (
    <>
      <PageHeader eyebrow="LIVE FIELD OPERATIONS" title="Live map"
        subtitle={`${activeCount} karyakartas online · ${sosCount} active SOS · updates every 15s`}
        actions={<button className="primary danger-btn" data-testid="raise-sos-button" onClick={raiseSos}><Siren size={16} /> Raise SOS</button>} />

      {error && <div className="notice-strip" data-testid="live-map-notice"><Radio size={16} /><span>{error}</span></div>}

      <div className="livemap-grid">
        <section className="panel" style={{ padding: 0, overflow: "hidden" }} data-testid="live-map-container">
          <MapContainer center={selfPos || NABADWIP} zoom={14} style={{ height: 500, width: "100%" }}>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {users.map((u) => (
              <Marker key={u.user_id} position={[u.lat, u.lng]} icon={iconFor(u.status)}>
                <Popup>
                  <b>{u.name}</b><br />
                  <small>{u.role} · {u.status}</small><br />
                  <small>{new Date(u.updated_at).toLocaleTimeString()}</small>
                </Popup>
              </Marker>
            ))}
            {sos.map((s) => (
              <Circle key={s.sos_id} center={[s.lat, s.lng]} radius={200} pathOptions={{ color: "#bd4f46", fillColor: "#bd4f46", fillOpacity: 0.2 }}>
                <Popup>
                  <b style={{ color: "#bd4f46" }}>SOS · {s.name}</b><br />
                  <small>{new Date(s.created_at).toLocaleString()}</small><br />
                  <button className="link" onClick={() => resolveSos(s.sos_id)}>Mark resolved</button>
                </Popup>
              </Circle>
            ))}
          </MapContainer>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2><Flame size={16} /> Booth workload heatmap</h2>
            <p>
              {workload.totals?.booths || 0} booths · {workload.totals?.open_complaints || 0} open · {workload.totals?.hot || 0} hot
            </p>
          </div>
          {(!workload.booths || workload.booths.length === 0) ? (
            <p className="hint" style={{ padding: 16 }}>No booth activity yet. Data will populate once complaints or volunteers exist.</p>
          ) : (
            <div className="booth-heat" data-testid="booth-heatmap">
              {workload.booths.map((b) => (
                <button
                  type="button"
                  key={b.booth}
                  className={`booth-heat-cell band-${b.band}`}
                  data-testid={`booth-heat-${b.booth}`}
                  title={`Booth ${b.booth} · Ward ${b.ward} · ${b.open_complaints} open / ${b.volunteers} active volunteer${b.volunteers === 1 ? "" : "s"}${b.next_assignee ? ` · Next: ${b.next_assignee}` : ""} — click to open triage drawer`}
                  onClick={() => setDrawer({ booth: b.booth, ward: b.ward, band: b.band })}
                >
                  <div className="booth-heat-head">
                    <b>Booth {b.booth}</b>
                    <span className="chip" data-testid={`booth-band-${b.booth}`}>{b.band.toUpperCase()}</span>
                  </div>
                  <div className="booth-heat-body">
                    <span>{b.open_complaints} open</span>
                    <span>·</span>
                    <span>{b.volunteers} vol{b.volunteers === 1 ? "" : "s"}</span>
                  </div>
                  {b.next_assignee ? (
                    <small>Next: {b.next_assignee}{b.next_assignee_open !== null ? ` (${b.next_assignee_open} open)` : ""}</small>
                  ) : (
                    <small style={{ color: "#b91c1c" }}>⚠ No volunteer</small>
                  )}
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="panel">
          <div className="panel-head"><h2>Team online</h2><p>Last 5 minutes</p></div>
          <div className="live-list">
            {users.length === 0 && <p className="hint" style={{ padding: 18 }}>No pings yet. Ask team to open the app.</p>}
            {users.map((u, i) => (
              <div key={u.user_id} className="live-row" data-testid={`live-user-${i}`}>
                <span className={`status-dot ${u.status === "sos" ? "red" : "green"}`} />
                <div>
                  <b>{u.name}</b>
                  <small>{u.role} · {new Date(u.updated_at).toLocaleTimeString()}</small>
                </div>
                <small>{u.accuracy ? `±${Math.round(u.accuracy)}m` : ""}</small>
              </div>
            ))}
          </div>

          {sos.length > 0 && (
            <>
              <div className="panel-head" style={{ borderTop: "1px solid var(--line)" }}><h2 style={{ color: "#bd4f46" }}>Active SOS ({sos.length})</h2></div>
              {sos.map((s, i) => (
                <div key={s.sos_id} className="live-row sos-row" data-testid={`sos-incident-${i}`}>
                  <span className="status-dot red" />
                  <div>
                    <b>{s.name}</b>
                    <small>{new Date(s.created_at).toLocaleString()} · {s.lat.toFixed(4)}, {s.lng.toFixed(4)}</small>
                  </div>
                  <button className="link" data-testid={`resolve-sos-${i}`} onClick={() => resolveSos(s.sos_id)}>Resolve</button>
                </div>
              ))}
            </>
          )}
        </section>
      </div>

      {drawer && (
        <div className="booth-drawer-backdrop" data-testid="booth-drawer-backdrop"
             onClick={(e) => { if (e.target === e.currentTarget) setDrawer(null); }}>
          <aside className={`booth-drawer band-${drawer.band}`} data-testid="booth-drawer">
            <header className="booth-drawer-head">
              <div>
                <span className="hint" style={{ fontSize: 11, letterSpacing: ".6px", textTransform: "uppercase" }}>Booth triage</span>
                <h2 style={{ margin: "4px 0 0" }}>Booth {drawer.booth}</h2>
                <small className="hint">Ward {drawer.ward ?? "—"} · <span style={{ textTransform: "uppercase", fontWeight: 700 }}>{drawer.band}</span></small>
              </div>
              <button className="icon-button" data-testid="booth-drawer-close" onClick={() => setDrawer(null)} aria-label="Close drawer">
                <X size={18} />
              </button>
            </header>

            {!drawerDetail ? (
              <p className="hint" style={{ padding: 32, textAlign: "center" }}>Loading booth detail…</p>
            ) : drawerDetail.error ? (
              <p className="hint" style={{ padding: 24, color: "#b04a3f" }}>{drawerDetail.error}</p>
            ) : (
              <>
                <div className="booth-drawer-body">
                <section className="booth-drawer-section" data-testid="drawer-next-assignee">
                  <h3>Next assignee</h3>
                  {drawerDetail.next_assignee ? (
                    <div className="drawer-row">
                      <span className="avatar" style={{ background: "#0f766e", color: "white" }}>
                        {drawerDetail.next_assignee.name?.trim().charAt(0).toUpperCase()}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <PersonLink contactId={drawerDetail.next_assignee.contact_id} name={drawerDetail.next_assignee.name} testid="drawer-next-link">
                          <b>{drawerDetail.next_assignee.name}</b>
                        </PersonLink>
                        <small style={{ display: "block", color: "var(--muted)" }}>
                          {drawerDetail.next_assignee.role || "Karyakarta"} · {drawerDetail.next_assignee.open_complaints} open · {drawerDetail.next_assignee.points} pts
                        </small>
                      </div>
                    </div>
                  ) : (
                    <p className="hint" style={{ color: "#b04a3f", margin: 0 }}>⚠ No active volunteer in this booth — assign manually.</p>
                  )}
                </section>

                <section className="booth-drawer-section" data-testid="drawer-volunteers">
                  <h3><Users size={14} /> Active volunteers ({drawerDetail.volunteer_count})</h3>
                  {drawerDetail.volunteers.length === 0 ? (
                    <p className="hint" style={{ margin: 0 }}>None active right now.</p>
                  ) : (
                    <ul className="drawer-vol-list">
                      {drawerDetail.volunteers.map((v, i) => (
                        <li key={v.volunteer_id} className="drawer-vol-row" data-testid={`drawer-vol-${i}`}>
                          <PersonLink contactId={v.contact_id} name={v.name}>
                            <b>{v.name}</b>
                          </PersonLink>
                          <small>{v.role} · {v.points || 0} pts</small>
                          <span className={`chip ${v.open_complaints_count === 0 ? "green" : v.open_complaints_count > 5 ? "red" : v.open_complaints_count > 2 ? "amber" : "blue"}`}>
                            {v.open_complaints_count === 0 ? "Free" : `${v.open_complaints_count} open`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                <section className="booth-drawer-section" data-testid="drawer-recent">
                  <h3>Last 5 open complaints</h3>
                  {drawerDetail.recent_complaints.length === 0 ? (
                    <p className="hint" style={{ margin: 0 }}>No open complaints — this booth is clear.</p>
                  ) : (
                    <ul className="drawer-comp-list">
                      {drawerDetail.recent_complaints.map((c, i) => (
                        <li key={c.complaint_id} className="drawer-comp-row" data-testid={`drawer-comp-${i}`}>
                          <div className="drawer-comp-head">
                            <span className={`chip ${{ Urgent: "red", High: "amber", Medium: "blue", Low: "green" }[c.priority] || "blue"}`}>{c.priority}</span>
                            <b>{c.complaint_id}</b>
                            <span className="drawer-comp-title">{c.title}</span>
                          </div>
                          <small className="hint">
                            {c.category} · <PersonLink contactId={c.assignee_contact_id} name={c.assignee}>{c.assignee}</PersonLink> · {new Date(c.created_at).toLocaleDateString()}
                          </small>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
                </div>

                <footer className="booth-drawer-foot">
                  <button
                    className="primary"
                    data-testid="drawer-open-complaints"
                    onClick={() => {
                      if (setPageContext && setPage) {
                        setPageContext({ booth: drawer.booth, ward: drawer.ward, from: "livemap" });
                        setPage("complaints");
                      }
                      setDrawer(null);
                    }}
                  >
                    <ArrowUpRight size={14} /> Open all in Complaints ({drawerDetail.open_complaints})
                  </button>
                </footer>
              </>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
